import { filter } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { format, parseISO, addWeeks, isAfter, isWithinInterval } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  TablePagination,
  Skeleton,
  Box
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from '../../redux/store';
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead } from '../../components/_dashboard/user/list';
import AlertDialog from './admin/AlertDialog';
import TaskItemModal from './TaskItemModal';
import { getTaskByItemIdWithUserStatus } from '../../redux/slices/tasks';

const LINES_TO_SHOW = 1;

const useStyles = makeStyles({
  multiLineEllipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': LINES_TO_SHOW,
    '-webkit-box-orient': 'vertical'
  }
});

const TABLE_HEAD = [
  { id: 'task_name', label: 'Task Name', alignRight: false },
  { id: 'date_start', label: 'Start Date', alignRight: false },
  { id: 'date_expired', label: 'Task Expired', alignRight: false },
  { id: 'date_complted', label: 'Date Completed', alignRight: false },
  { id: 'completed_by', label: 'Completed By', alignRight: false },
  { id: 'notes', label: 'Notes', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: '' }
];

const SkeletonLoad = (
  <>
    <Box sx={{ width: '100%' }}>
      {[...Array(3)].map((_, index) => (
        <Skeleton key={index} sx={{ paddingTop: '5%', marginLeft: 2, marginRight: 2 }} />
      ))}
      <Skeleton animation="wave" sx={{ paddingTop: '5%', marginLeft: 2, marginRight: 2 }} />
      <Skeleton animation={false} sx={{ paddingTop: '5%', marginLeft: 2, marginRight: 2 }} />
    </Box>
  </>
);

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => _user.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

TaskItemsListing.propTypes = {
  itemId: PropTypes.string
};

export default function TaskItemsListing({ itemId }) {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { taskList, isLoading } = useSelector((state) => state.tasks);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [dialogStatus, setDialogStatus] = useState(false);
  const [rowDetails, setRowDetails] = useState({});

  const handleRequestSort = (_event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = taskList.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - taskList.length) : 0;

  const filteredUsers = applySortFilter(taskList, getComparator(order, orderBy));

  const isUserNotFound = filteredUsers.length === 0;

  const handleClose = () => {
    setDialogStatus(false);
  };

  const handleConfrim = () => {
    setDialogStatus(false);
  };

  const [open, setOpen] = useState(false);

  const callback = () => {
    dispatch(getTaskByItemIdWithUserStatus(itemId));
    setOpen(false);
  };

  const handleClickOpen = (row) => {
    setRowDetails(row);
    setOpen(true);
  };

  return (
    <>
      {!isLoading || filteredUsers.length > 0 ? (
        <Page style={{ marginTop: '50px' }}>
          <Card>
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={taskList.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    isCheckBoxRequired={false}
                  />
                  <TableBody>
                    {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                      const { id, name, statusByUsers, startDate, expirationTime } = row;
                      const taskStartDate = format(parseISO(startDate), 'MM/dd/yyyy');
                      const expiryDate = format(addWeeks(parseISO(startDate), expirationTime), 'MM/dd/yyyy');
                      const isExpired = isAfter(new Date(), expiryDate);
                      const isFuture = isAfter(parseISO(startDate), new Date());
                      const isCurrent = isWithinInterval(new Date(), {
                        start: parseISO(startDate),
                        end: parseISO(expiryDate)
                      });

                      let status = statusByUsers?.status;
                      let color;
                      if (statusByUsers?.status) {
                        color = 'success';
                        status = 'Completed';
                      } else if (isExpired) {
                        status = 'Expired';
                        color = 'error';
                      } else if (isFuture) {
                        status = 'Upcoming';
                        color = 'default';
                      } else if (isCurrent) {
                        status = 'Current';
                        color = 'secondary';
                      }
                      const isItemSelected = selected.indexOf(id) !== -1;
                      return (
                        <TableRow
                          style={{ cursor: 'pointer' }}
                          hover
                          key={id}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                          onClick={() => handleClickOpen(row)}
                        >
                          <TableCell component="th" scope="row" padding="none">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Typography variant="subtitle2" noWrap>
                                {name}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell align="left">
                            <Typography variant="subtitle2" noWrap>
                              {taskStartDate}
                            </Typography>
                          </TableCell>

                          <TableCell align="left">
                            <Typography variant="subtitle2" noWrap>
                              {status === 'Expired' ? expiryDate : '-'}
                            </Typography>
                          </TableCell>

                          <TableCell align="left">
                            <Typography variant="subtitle2" noWrap>
                              {statusByUsers?.dateCompleted ? statusByUsers?.dateCompleted : '-'}
                            </Typography>
                          </TableCell>

                          <TableCell align="left">
                            <Typography variant="subtitle2" noWrap>
                              {statusByUsers?.completedBy ? statusByUsers?.completedBy : '-'}
                            </Typography>
                          </TableCell>

                          <TableCell align="left">
                            <Typography variant="subtitle2" className={classes.multiLineEllipsis}>
                              {statusByUsers?.notes ? statusByUsers?.notes : '-'}
                            </Typography>
                          </TableCell>

                          <TableCell align="left">
                            {color ? (
                              <Label variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'} color={color}>
                                {status}
                              </Label>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                  {isUserNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                          <SearchNotFound />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={taskList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <AlertDialog dialogStatus={dialogStatus} handleClose={handleClose} handleConfrim={handleConfrim} />
            <TaskItemModal open={open} parentCallBack={callback} currentData={rowDetails} />
          </Card>
        </Page>
      ) : (
        SkeletonLoad
      )}
    </>
  );
}
